import React, { useEffect } from 'react';
import { colors } from '../styles/variables';
import CourseHeader from '../components/course/CourseHeader';
import CourseAbout from '../components/course/CourseAbout';
import CourseCurriculum from '../components/course/CourseCurriculum';
import CourseProjects from '../components/course/CourseProjects';
import CourseInstructors from '../components/course/CourseInstructors';
import CourseCta from '../components/course/CourseCta';
import CourseReviewSection from '../components/course/CourseReviewSection';
import Seo from '../components/common/Seo';
import { projectsByCourse } from '../data/projects';

const courseJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'Course',
  name: 'AI 실무 코딩',
  description: 'Copilot, Cursor, Claude 등 최신 AI 코딩 도구를 활용한 실전 워크플로우 1:1 멘토링.',
  provider: {
    '@type': 'EducationalOrganization',
    name: 'ElectroWave Club',
    sameAs: 'https://electrowave.kr',
  },
  inLanguage: 'ko',
  educationalLevel: 'advanced',
  url: 'https://electrowave.kr/ai-coding',
};

const AiCodingPage = () => {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  // 코스 헤더 데이터
  const headerData = {
    tag: 'AI 개발',
    title: 'AI(Vibe Coding) 과정',
    description: '최신 AI 도구와 에이전트 워크플로를 활용해 실제 서비스와 운영용 콘솔을 빠르게 설계하고 구현하는 방법을 학습합니다. 단순 클론을 넘어 배포 가능한 포트폴리오를 직접 완성합니다.',
    duration: '총 24시간 / 6주 과정',
    level: '초중급자 대상',
    bgColor: colors.aiColor,
    bgImage: '/assets/images/course-thumb-ai-coding.png'
  };
  
  // 코스 소개 데이터
  const aboutData = {
    subtitle: 'AI 시대의 새로운 개발 패러다임을 배우고, 실제 운영 가능한 서비스와 내부 운영 도구를 AI와 함께 설계·구현하는 방법을 익히는 과정입니다.',
    forWhom: [
      '프로그래밍 기초 지식이 있는 개발자',
      'AI를 실무 개발 흐름에 연결하고 싶은 분',
      '포트폴리오로 보여줄 서비스형 결과물이 필요한 분',
      '관리자 콘솔, 자동화 파이프라인, 추천 서비스 구축에 관심 있는 분',
      'Cursor, Claude, Copilot 기반 협업 방식을 익히고 싶은 분',
      '프로젝트 개발 속도와 품질을 동시에 끌어올리고 싶은 분'
    ],
    skills: [
      'AI와 협업하는 요구사항 분석 및 화면 설계',
      'Cursor/Copilot 기반 구현 가속과 리팩터링',
      'Claude를 활용한 시스템 아키텍처 설계',
      '추천 서비스, 운영 콘솔, 콘텐츠 파이프라인 설계 역량',
      '프롬프트 운영, 모델 관리, 자동화 워크플로 구축 능력',
      '배포 가능한 포트폴리오 수준의 서비스 완성 경험'
    ],
    imageSrc: '/assets/images/course-thumb-ai-coding.png'
  };
  
  // 커리큘럼 데이터
  const curriculumData = {
    subtitle: '실제 서비스 수준의 프로젝트를 구현하면서 AI 도구 활용법을 체계적으로 학습합니다.',
    modules: [
      {
        title: '1. 최신 AI 개발 도구 소개',
        description: '현대 개발에서 활용되는 주요 AI 도구들의 특징과 장단점을 학습합니다.',
        lessons: [
          'Microsoft Copilot의 이해와 활용',
          'Cursor IDE 설정과 기능 소개',
          'Claude의 특징과 활용 방안',
          'AI 도구별 최적 사용 시나리오'
        ]
      },
      {
        title: '2. Microsoft Copilot 활용',
        description: 'Microsoft Copilot을 활용한 효율적인 코드 작성과 문제 해결 방법을 배웁니다.',
        lessons: [
          '코드 생성과 리팩토링',
          'API 및 데이터 모델 설계',
          '테스트 코드 자동화',
          '문서화 및 주석 생성'
        ]
      },
      {
        title: '3. Cursor IDE 마스터하기',
        description: 'Cursor IDE의 AI 기능을 활용한 효율적인 개발 방법을 학습합니다.',
        lessons: [
          'AI 기반 코드 자동 완성',
          '실시간 코드 리뷰와 개선',
          '버그 감지 및 수정',
          '코드 최적화 제안'
        ]
      },
      {
        title: '4. Claude와 함께하는 설계',
        description: 'Claude를 활용하여 프로젝트 설계와 아키텍처를 개선하는 방법을 배웁니다.',
        lessons: [
          '시스템 아키텍처 설계',
          '데이터베이스 모델링',
          'API 엔드포인트 설계',
          '보안 및 성능 최적화'
        ]
      },
      {
        title: '5. 실전 프로젝트 준비',
        description: '대규모 웹 서비스 클론 프로젝트를 위한 준비 과정을 진행합니다.',
        lessons: [
          '프로젝트 요구사항 분석',
          '기술 스택 선정',
          '개발 환경 설정',
          '작업 분배 및 일정 계획'
        ]
      },
      {
        title: '6. 클론 프로젝트 구현',
        description: 'AI 도구를 활용하여 실제 서비스 수준의 프로젝트를 구현합니다.',
        lessons: [
          '컴포넌트 설계 및 구현',
          '상태 관리 및 데이터 흐름',
          'UI/UX 최적화',
          '배포 및 성능 테스트'
        ]
      }
    ]
  };
  
  // 프로젝트 데이터
  const projectsData = {
    subtitle: '실제 공개 포트폴리오와 유사한 수준의 서비스, 운영 콘솔, 자동화 워크플로를 구현하며 실전 경험을 쌓습니다.',
    projects: projectsByCourse['ai-coding'],
  };
  
  // 강사진 데이터
  const instructorsData = {
    subtitle: 'AI 도구를 활용한 대규모 서비스 개발 경험이 풍부한 현업 개발자들이 여러분의 성장을 돕습니다.',
    instructors: [
      {
        name: 'Rechard Park',
        title: '시니어 풀스택 개발자',
        bio: '대규모 스트리밍 플랫폼 개발 경험을 보유한 전문가로, Microsoft Copilot과 Cursor를 활용한 개발 프로세스 최적화에 깊은 이해를 가지고 있습니다. 여러 스타트업의 기술 고문으로 활동 중입니다.',
        image: '/assets/images/instructor-park-jiwon.png'
      },
      {
        name: 'Lisa Choi',
        title: '프론트엔드 아키텍트',
        bio: '대형 숙박 플랫폼 프론트엔드 개발 경험이 풍부하며, AI 도구를 활용한 개발 생산성 향상 분야의 전문가입니다. 특히 마이크로 프론트엔드 아키텍처와 성능 최적화 분야에서 뛰어난 실력을 보유하고 있습니다.',
        image: '/assets/images/instructor-choi-yujin.png'
      }
    ]
  };
  
  // CTA 데이터
  const ctaData = {
    title: 'AI 코딩으로 개발 생산성을 높이세요',
    subtitle: 'ElectroWave Club의 AI 코딩 과정을 통해 최신 AI 도구를 활용한 효율적인 개발 방법을 배우고 실무에서 바로 적용하세요.',
    bgColor: colors.aiColor,
    endColor: '#311B92'  // 진한 보라색
  };
  
  return (
    <>
      <Seo
        title="AI 실무 코딩"
        description="Copilot, Cursor, Claude 등 최신 AI 코딩 도구를 활용한 실전 워크플로우 1:1 멘토링."
        path="/ai-coding"
        ogImage="/assets/images/course-thumb-ai-coding.png"
        ogType="article"
        jsonLd={courseJsonLd}
      />
      <CourseHeader {...headerData} />
      <CourseAbout {...aboutData} />
      <CourseCurriculum {...curriculumData} />
      <CourseProjects {...projectsData} courseSlug="ai-coding" />
      <CourseInstructors {...instructorsData} />
      <CourseReviewSection courseTitle="AI 코딩" />
      <CourseCta {...ctaData} />
    </>
  );
};

export default AiCodingPage; 
